import { useEffect, useState } from "react";
import { toZonedTime } from "date-fns-tz";
import { DEFAULT_TIMEZONE } from "../Constants/constant";

const CHECK_INTERVAL = 20 * 1000; // 20s — slot boundaries are minute-precise

function resolveType(ad) {
  if (ad.adType) return ad.adType;
  // Legacy fallback for ads created before adType existed
  if (ad.activeFrom || ad.activeTo) return "date_range";
  return "always";
}

function isAdActive(ad) {
  const now = toZonedTime(new Date(), DEFAULT_TIMEZONE);
  const type = resolveType(ad);

  if (type === "always") return true;

  if (type === "date_range") {
    if (ad.activeFrom) {
      const from = toZonedTime(new Date(ad.activeFrom), DEFAULT_TIMEZONE);
      if (now < from) return false;
    }
    if (ad.activeTo) {
      const to = toZonedTime(new Date(ad.activeTo), DEFAULT_TIMEZONE);
      if (now > to) return false;
    }
    return true;
  }

  if (type === "daily_slot") {
    if (!ad.slotStart || !ad.slotEnd) return false;
    const cur = now.getHours() * 60 + now.getMinutes();
    const [sh, sm] = ad.slotStart.split(":").map(Number);
    const [eh, em] = ad.slotEnd.split(":").map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    return cur >= start && cur < end;
  }

  return false;
}

/**
 * daily_slot is exclusive — if any active, only those show.
 * date_range + always pool together and cycle when no slot is active.
 */
function getActiveAdsByPriority(ads) {
  const slots = [];
  const dateRanges = [];
  const always = [];

  for (const ad of ads) {
    const type = resolveType(ad);
    if (type === "daily_slot" && isAdActive(ad)) slots.push(ad);
    else if (type === "date_range" && isAdActive(ad)) dateRanges.push(ad);
    else if (type === "always") always.push(ad);
  }

  if (slots.length > 0) return slots;
  return [...dateRanges, ...always];
}

export function useActiveAds(advertisements = []) {
  const [activeAds, setActiveAds] = useState(() =>
    getActiveAdsByPriority(advertisements)
  );

  useEffect(() => {
    setActiveAds(getActiveAdsByPriority(advertisements));

    const interval = setInterval(() => {
      setActiveAds(getActiveAdsByPriority(advertisements));
    }, CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [advertisements]);

  return activeAds;
}
